import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
    AuthService,
    ToastService,
    WishService
} from '@app/core';
import {
    CustomField,
    CustomFieldAnswer,
    CustomFieldChoice,
    Period,
    Settings,
    Wish
} from '@app/shared';


@Component({
    selector: 'app-wish',
    templateUrl: './wish.component.html',
    styleUrls: ['./wish.component.css']
})
export class WishComponent implements OnInit {

    evening_visitation_values: number[] = [];
    evening_drive_values: number[] = [];
    period: Period;
    wish: Wish = null;
    customFields: CustomField[] = [];
    private _mode: 'create' | 'update';
    private _remainingChars = 300;
    private _settings: Settings;

    constructor(
        private authService: AuthService,
        private wishService: WishService,
        private route: ActivatedRoute,
        private router: Router,
        private toast: ToastService
    ) { }

    ngOnInit() {
        this.route.data.subscribe(data => {
            this._settings = data.settings;
            this.period = data.period;
            this.wish = data.wish;
            this.customFields = data.customFields || [];
            this.prepareSettings(this._settings);
            this.prepare();
            this.ensureAnswers();
        });
    }

    get isStudent() {
        const type = this.authService.user.profile.doctor_type;
        return type === 4 || type === 5 || type === 6;
    }

    get remainingChars() {
        return this._remainingChars;
    }

    get commentLength() {
        return this._settings.comment_length;
    }

    get requiredUnanswered() {
        // Any required field left unanswered blocks the fields below it.
        // "No shifts" opts out of everything, so it never blocks.
        if (this.wish.no_shifts) {
            return false;
        }
        return this.customFields.some(
            field => field.is_required && !this.isAnswered(field));
    }

    save() {
        if (this.wish.voluntary && this.wish.imposed) {
            this.toast.error('Kan ikke vælge både frivillig og pålagt');
            return;
        }
        if (this.wish.no_shifts) {
            // Resets other selected values when no shifts is selected
            this.noShifts(this.wish);
        }
        if (this._mode === 'create') {
            this.create();
        } else if (this._mode === 'update') {
            this.update();
        }
    }

    updateComment(comment: string) {
        this.calculateRemainingChars(comment);
        this.wish.comment = comment;
    }

    setVoluntary(value: boolean) {
        // Voluntary and imposed are mutually exclusive — only one may be on.
        this.wish.voluntary = value;
        if (value) {
            this.wish.imposed = false;
        }
    }

    setImposed(value: boolean) {
        this.wish.imposed = value;
        if (value) {
            this.wish.voluntary = false;
        }
    }

    isSelected(field: CustomField, choice: CustomFieldChoice) {
        const answer = this.getAnswer(field);
        return !!answer && answer.choices.indexOf(choice.id) > -1;
    }

    toggleChoice(field: CustomField, choice: CustomFieldChoice) {
        const answer = this.getAnswer(field);
        if (!answer) {
            return;
        }
        const index = answer.choices.indexOf(choice.id);
        if (index > -1) {
            answer.choices.splice(index, 1);
        } else {
            answer.choices.push(choice.id);
        }
    }

    selectRadio(field: CustomField, choice: CustomFieldChoice) {
        // Radio is single-select: replace the whole selection.
        const answer = this.getAnswer(field);
        if (answer) {
            answer.choices = [choice.id];
        }
    }

    answerValue(field: CustomField): string {
        const answer = this.getAnswer(field);
        return answer ? answer.value : '';
    }

    setValue(field: CustomField, value: string) {
        const answer = this.getAnswer(field);
        if (answer) {
            answer.value = value;
        }
    }


    private prepare() {
        if (!this.wish) {
            this.wish = new Wish();
            this.wish.period = this.period.id;
            this.wish.evening_visitation = Math.min(...this.evening_visitation_values);
            this.wish.evening_drive = Math.min(...this.evening_drive_values);

        }
        this._mode = !!this.wish.id ? 'update' : 'create';
        this.calculateRemainingChars(this.wish.comment || '');
    }

    private ensureAnswers() {
        if (!this.wish.custom_answers) {
            this.wish.custom_answers = [];
        }
        // Every rendered field needs an answer object to bind to; seed from
        // any existing answers when editing, create a blank one otherwise.
        this.customFields.forEach(field => {
            if (!this.getAnswer(field)) {
                const answer = new CustomFieldAnswer();
                answer.custom_field = field.id;
                this.wish.custom_answers.push(answer);
            }
        });
    }

    private getAnswer(field: CustomField): CustomFieldAnswer {
        return this.wish.custom_answers.find(a => a.custom_field === field.id);
    }

    private isAnswered(field: CustomField): boolean {
        const answer = this.getAnswer(field);
        if (!answer) {
            return false;
        }
        if (field.field_type === 'text' || field.field_type === 'textarea') {
            return answer.value.trim().length > 0;
        }
        return answer.choices.length > 0;
    }


    private prepareSettings(settings: Settings) {
        this.evening_visitation_values = this.getRange(settings.evening_visitation_min, settings.evening_visitation_max);
        this.evening_drive_values = this.getRange(settings.evening_drive_min, settings.evening_drive_max);
    }

    private calculateRemainingChars(text: string) {
        this._remainingChars = this._settings.comment_length - text.length;
    }

    private getRange(start: number, end: number) {
        const list: number[] = [];
        for (let i = start; i < end + 1; i++) {
            list.push(i);
        }
        return list;
    }

    private noShifts(wish: Wish) {
        wish.evening_visitation = 0;
        wish.evening_drive = 0;
        wish.comment = '';
        wish.no_shifts = true;
        wish.custom_answers = [];
    }

    private create() {
        this.wishService.create(this.wish).subscribe(
            wish => this.success(wish),
            error => this.fail(error)
        );
    }

    private update() {
        this.wishService.update(this.wish).subscribe(
            wish => this.success(wish),
            error => this.fail(error)
        );
    }

    private success(wish: Wish) {
        this.toast.success('Ønsker gemt');
        this.router.navigate(['wish-list']);
    }

    private fail(error: any) {
        const msg = error.error;
        this.toast.error('Fejl: Ønsker ikke gemt');
    }

}
